import { createReducer, on } from '@ngrx/store';
import { sortSessionTimes } from 'app/services/utils/time-utils';
import * as PlaydateActions from './playdate.actions';
import { Playdate } from './playdate.model';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface PlaydatesState {
    playdates: Playdate[];
    playdate: Playdate;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialPlaydatesState: PlaydatesState = {
    playdates: [],
    playdate: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All Playdates Reducer
export const playdatesReducer = createReducer(
    initialPlaydatesState,

    // On Set Playdates
    on(PlaydateActions.setCurrentVenuePlaydates, (state, { playdates }) => {
        const sortedPlaydates = playdates.map((pd) => ({
            ...pd,
            session_times: sortSessionTimes(pd.session_times),
        }));

        return {
            ...state,
            isLoading: false,
            playdates: sortedPlaydates,
        };
    }),

    // On Clear Playdates
    on(PlaydateActions.clearCurrentVenuePlaydates, (state) => {
        return {
            ...state,
            isLoading: false,
            playdates: [],
        };
    }),

    // On Load Playdates
    on(PlaydateActions.loadPlaydates, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(PlaydateActions.loadPlaydatesSuccess, (state, { playdates }) => ({
        ...state,
        isLoading: false,
        playdates,
    })),
    on(PlaydateActions.loadPlaydatesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Set Playdate
    on(PlaydateActions.setFinancesSelectedPlaydate, (state, { playdate }) => ({
        ...state,
        isLoading: false,
        playdate,
    })),

    // On Load Single Playdate
    on(PlaydateActions.loadPlaydate, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(PlaydateActions.loadPlaydateSuccess, (state, { playdate }) => ({
        ...state,
        isLoading: false,
        playdate,
    })),
    on(PlaydateActions.loadPlaydateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Create
    on(PlaydateActions.createPlaydate, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(PlaydateActions.createPlaydateSuccess, (state, { playdates }) => ({
        ...state,
        isLoading: false,
        playdates: [...state.playdates, ...playdates],
    })),
    on(PlaydateActions.createPlaydateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Update
    on(PlaydateActions.updatePlaydate, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(PlaydateActions.updatePlaydateSuccess, (state, { playdate }) => ({
        ...state,
        isLoading: false,
        playdates: state.playdates.map((p) =>
            p.id === playdate.id ? playdate : p
        ),
    })),
    on(PlaydateActions.updatePlaydateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Delete
    on(PlaydateActions.deletePlaydate, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(PlaydateActions.deletePlaydateSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        playdates: state.playdates.filter((p) => p.id !== id),
    })),
    on(PlaydateActions.deletePlaydateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Update Feature
    on(PlaydateActions.updatePlaydateFeatures, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        PlaydateActions.updatePlaydateFeaturesSuccess,
        (state, { playdate }) => ({
            ...state,
            isLoading: false,
            playdates: state.playdates.map((p) =>
                p.id === playdate.id ? playdate : p
            ),
        }) // By now, this is just an update as well
    ),
    on(PlaydateActions.updatePlaydateFeaturesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    }))
);
